import { useCallback, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  X, Package, RefreshCw, Undo2, AlertTriangle, ChevronDown, ChevronRight,
  Loader2, CheckCircle2, XCircle, CircleDot, ListChecks,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useWS } from '../contexts/WSContext'
import ConfirmDialog from './ConfirmDialog'
import { fmtDate } from '../i18n/format'

const MAX_PKGS = 16
const MAX_JOINED_BYTES = 256 // espelha o limite do backend/watcher

const joinedBytes = (names) => new TextEncoder().encode([...names].join(' ')).length

const STATUS_ICON = {
  accepted: { Icon: CircleDot, cls: 'text-sky-500' },
  started:  { Icon: Loader2, cls: 'text-amber-500 animate-spin' },
  success:  { Icon: CheckCircle2, cls: 'text-status-op' },
  failed:   { Icon: XCircle, cls: 'text-status-down' },
  rejected: { Icon: XCircle, cls: 'text-status-down' },
}

function SectionCard({ title, icon: Icon, action, children }) {
  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-700">
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
        <p className="flex items-center gap-1.5 text-xs font-semibold text-slate-600 dark:text-slate-300">
          <Icon size={13} className="text-slate-400" /> {title}
        </p>
        {action}
      </div>
      <div className="px-4 py-2">{children}</div>
    </div>
  )
}

// Modal de atualização OTA de pacotes do VMC. Mesmo esqueleto estrutural do
// AdvancedDeviceModal: portal para o body (o painel do Drawer tem translate-x,
// que aprisionaria um fixed), ESC fecha, scroll do body travado.
export default function UpdatePackagesModal({ deviceName, displayName, onClose }) {
  const { t } = useTranslation()
  const { updateStatuses } = useWS()

  const [info, setInfo] = useState(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(null)
  const [selected, setSelected] = useState(() => new Set())
  const [showInstalled, setShowInstalled] = useState(false)
  const [confirm, setConfirm] = useState(null) // { mode: 'selected' | 'all' }
  const [confirmChecked, setConfirmChecked] = useState(false)
  const [rollbackOpen, setRollbackOpen] = useState(false)
  const [sending, setSending] = useState(false)
  const [sendError, setSendError] = useState(null)

  const statuses = updateStatuses[deviceName] || []
  const shownName = displayName || deviceName

  const load = useCallback(async () => {
    setLoading(true)
    setLoadError(null)
    try {
      const res = await fetch(`/api/devices/${encodeURIComponent(deviceName)}/packages`)
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      setInfo(await res.json())
    } catch (e) {
      setLoadError(e.message)
    } finally {
      setLoading(false)
    }
  }, [deviceName])

  useEffect(() => { load() }, [load])

  // Uma atualização concluída muda as versões instaladas: refaz a consulta.
  const lastStatus = statuses.length ? statuses[statuses.length - 1] : null
  useEffect(() => {
    if (lastStatus?.status === 'success') load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastStatus])

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.() }
    document.addEventListener('keydown', onKey)
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = prev }
  }, [onClose])

  const updatable = info?.updatable || []
  const newPkgs = info?.new || []
  const installed = info?.device?.packages || []
  const deviceOk = !!info?.device?.ok

  // Depois de um refetch (ex.: pos-update) um nome pode nao estar mais em
  // updatable/new; remove-o da selecao para nao mandar um install indevido.
  useEffect(() => {
    const valid = new Set([...updatable.map(u => u.name), ...newPkgs.map(p => p.name)])
    setSelected(prev => {
      const next = new Set([...prev].filter(n => valid.has(n)))
      return next.size === prev.size ? prev : next
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [info])

  const atLimit = selected.size >= MAX_PKGS
  const atByteLimit = joinedBytes(selected) >= MAX_JOINED_BYTES
  const toggle = (name) => {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(name)) { next.delete(name); return next }
      if (next.size >= MAX_PKGS) return next
      // O backend e o watcher recusam a lista se ela passar de 256 bytes; barra
      // aqui em vez de deixar o operador descobrir com um 400 apos confirmar.
      if (joinedBytes([...next, name]) > MAX_JOINED_BYTES) return next
      next.add(name)
      return next
    })
  }
  const allUpdatableSelected = updatable.length > 0 && updatable.every(u => selected.has(u.name))
  const toggleAllUpdatable = () => {
    setSelected(prev => {
      const next = new Set(prev)
      if (allUpdatableSelected) {
        updatable.forEach(u => next.delete(u.name))
        return next
      }
      for (const u of updatable) {
        if (next.size >= MAX_PKGS) break
        next.add(u.name)
      }
      return next
    })
  }

  const send = async (ops) => {
    setSending(true)
    setSendError(null)
    try {
      for (const { action, packages } of ops) {
        const res = await fetch(`/api/devices/${encodeURIComponent(deviceName)}/update`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action, packages }),
        })
        const data = await res.json().catch(() => ({}))
        if (!res.ok || !data.accepted) throw new Error(data.error || t('ops:devices.update.rejected'))
      }
      setConfirm(null)
      setConfirmChecked(false)
      setSelected(new Set())
    } catch (e) {
      setSendError(e.message)
    } finally {
      setSending(false)
    }
  }

  // Pacotes ainda nao instalados vao como install; os demais como update. O
  // watcher recusa update de pacote ausente, entao a distincao e obrigatoria.
  const selectionOps = () => {
    const updNames = new Set(updatable.map(u => u.name))
    const sel = [...selected]
    const toInstall = sel.filter(n => !updNames.has(n))
    const toUpdate = sel.filter(n => updNames.has(n))
    const ops = []
    if (toInstall.length > 0) ops.push({ action: 'install', packages: toInstall })
    if (toUpdate.length > 0) ops.push({ action: 'update', packages: toUpdate })
    return ops
  }

  const sendRollback = async () => {
    const res = await fetch(`/api/devices/${encodeURIComponent(deviceName)}/rollback`, { method: 'POST' })
    const data = await res.json().catch(() => ({}))
    if (!res.ok || !data.accepted) throw new Error(data.error || t('ops:devices.update.rejected'))
  }

  const checkbox = (checked, onChange, disabled) => (
    <input type="checkbox" checked={checked} onChange={onChange} disabled={disabled}
      className="rounded border-slate-300 dark:border-slate-600 text-brand focus:ring-brand disabled:opacity-40" />
  )

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-900/50" onClick={onClose} />
      <div className="relative w-full max-w-4xl h-[85vh] flex flex-col bg-white dark:bg-slate-900 rounded-xl shadow-2xl border border-slate-200 dark:border-slate-700">
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center gap-3 min-w-0">
            <Package size={18} className="text-brand shrink-0" />
            <div className="min-w-0">
              <h2 className="font-semibold text-slate-800 dark:text-slate-100 truncate">{t('ops:devices.update.title')}</h2>
              <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{shownName !== deviceName ? `${shownName} · ` : ''}{deviceName}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 shrink-0"><X size={18} /></button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto scrollbar-thin p-5 space-y-4">
          {loading && (
            <p className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
              <Loader2 size={14} className="animate-spin" /> {t('ops:devices.update.loading')}
            </p>
          )}
          {!loading && loadError && (
            <p className="text-sm text-status-down">{t('ops:devices.update.load_failed', { error: loadError })}</p>
          )}

          {!loading && info && (
            <>
              {!deviceOk && (
                <div className="flex items-start gap-2 rounded-lg border border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-300">
                  <AlertTriangle size={14} className="shrink-0 mt-0.5" /> {t('ops:devices.update.device_unreachable')}
                </div>
              )}
              {info.repo_error && (
                <div className="flex items-start gap-2 rounded-lg border border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-300">
                  <AlertTriangle size={14} className="shrink-0 mt-0.5" /> {t('ops:devices.update.repo_unreachable')}
                </div>
              )}

              <SectionCard
                title={t('ops:devices.update.updates_available')}
                icon={RefreshCw}
                action={updatable.length > 0 && (
                  <label className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400 cursor-pointer">
                    {checkbox(allUpdatableSelected, toggleAllUpdatable)}
                    {t('ops:devices.update.select_all')}
                  </label>
                )}
              >
                {updatable.length === 0 ? (
                  <p className="text-xs text-slate-400 dark:text-slate-500 py-1">{t('ops:devices.update.no_updates')}</p>
                ) : (
                  <div className="divide-y divide-slate-100 dark:divide-slate-800">
                    {updatable.map(u => (
                      <label key={u.name} className="flex items-center gap-3 py-1.5 text-xs cursor-pointer">
                        {checkbox(selected.has(u.name), () => toggle(u.name), !selected.has(u.name) && atLimit)}
                        <span className="font-mono font-medium text-slate-700 dark:text-slate-200">{u.name}</span>
                        <span className="ml-auto font-mono text-slate-400 dark:text-slate-500">{u.installed}</span>
                        <span className="text-slate-400 dark:text-slate-500">→</span>
                        <span className="font-mono text-status-op">{u.available}</span>
                      </label>
                    ))}
                  </div>
                )}
              </SectionCard>

              <SectionCard title={t('ops:devices.update.new_packages')} icon={Package}>
                {newPkgs.length === 0 ? (
                  <p className="text-xs text-slate-400 dark:text-slate-500 py-1">{t('ops:devices.update.no_new')}</p>
                ) : (
                  <div className="divide-y divide-slate-100 dark:divide-slate-800">
                    {newPkgs.map(p => (
                      <label key={p.name} className="flex items-center gap-3 py-1.5 text-xs cursor-pointer">
                        {checkbox(selected.has(p.name), () => toggle(p.name), !selected.has(p.name) && atLimit)}
                        <span className="font-mono font-medium text-slate-700 dark:text-slate-200">{p.name}</span>
                        <span className="ml-auto font-mono text-slate-400 dark:text-slate-500">{p.version}</span>
                      </label>
                    ))}
                  </div>
                )}
              </SectionCard>

              <div className="rounded-lg border border-slate-200 dark:border-slate-700">
                <button onClick={() => setShowInstalled(v => !v)}
                  className="w-full flex items-center justify-between px-4 py-2.5 text-xs font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800/50">
                  <span className="flex items-center gap-1.5">
                    {showInstalled ? <ChevronDown size={13} className="text-slate-400" /> : <ChevronRight size={13} className="text-slate-400" />}
                    {t('ops:devices.update.installed_packages')}
                  </span>
                  <span className="text-slate-400 dark:text-slate-500 font-normal">
                    {t('ops:devices.update.installed_count', { count: installed.length })}
                  </span>
                </button>
                {showInstalled && (
                  <div className="px-4 pb-2 border-t border-slate-200 dark:border-slate-700 max-h-56 overflow-y-auto scrollbar-thin divide-y divide-slate-100 dark:divide-slate-800">
                    {installed.map(p => (
                      <div key={p.name} className="flex items-center gap-3 py-1.5 text-xs">
                        <span className="font-mono text-slate-700 dark:text-slate-200">{p.name}</span>
                        <span className="ml-auto font-mono text-slate-400 dark:text-slate-500">{p.version}</span>
                        <span className="text-slate-400 dark:text-slate-500 w-14 text-right">{p.arch}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}

          <SectionCard title={t('ops:devices.update.timeline_title')} icon={ListChecks}>
            {statuses.length === 0 ? (
              <p className="text-xs text-slate-400 dark:text-slate-500 py-1">{t('ops:devices.update.timeline_empty')}</p>
            ) : (
              <div className="divide-y divide-slate-100 dark:divide-slate-800">
                {statuses.map((s, i) => {
                  const st = STATUS_ICON[s.status] || STATUS_ICON.accepted
                  return (
                    <div key={i} className="flex items-start gap-2 py-1.5 text-xs">
                      <st.Icon size={14} className={`shrink-0 mt-0.5 ${st.cls}`} />
                      <div className="min-w-0">
                        <span className="font-medium text-slate-700 dark:text-slate-200">
                          {t(`ops:devices.update.action.${s.action}`, { defaultValue: s.action })}
                          {' · '}
                          {t(`ops:devices.update.status.${s.status}`, { defaultValue: s.status })}
                        </span>
                        {s.packages?.length > 0 && (
                          <span className="ml-2 font-mono text-slate-400 dark:text-slate-500 break-all">{s.packages.join(', ')}</span>
                        )}
                        {s.detail && <p className="text-slate-400 dark:text-slate-500">{s.detail}</p>}
                      </div>
                      <span className="ml-auto shrink-0 text-slate-400 dark:text-slate-500 tabular-nums">
                        {fmtDate(s.ts, { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                      </span>
                    </div>
                  )
                })}
              </div>
            )}
          </SectionCard>
        </div>

        <div className="border-t border-slate-200 dark:border-slate-700 px-5 py-3">
          {confirm ? (
            <div className="space-y-2">
              <div className="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-200">
                <AlertTriangle size={15} className="text-amber-500 shrink-0 mt-0.5" />
                <span>
                  {confirm.mode === 'all'
                    ? t('ops:devices.update.confirm_all', { name: shownName })
                    : t('ops:devices.update.confirm_selected', { count: selected.size, name: shownName })}
                </span>
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400">{t('ops:devices.update.confirm_warning')}</p>
              <label className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300 cursor-pointer">
                {checkbox(confirmChecked, () => setConfirmChecked(v => !v))}
                {t('ops:devices.update.confirm_checkbox')}
              </label>
              {sendError && <p className="text-xs text-status-down">{t('ops:devices.update.send_failed', { error: sendError })}</p>}
              <div className="flex justify-end gap-2">
                <button onClick={() => { setConfirm(null); setConfirmChecked(false); setSendError(null) }} disabled={sending}
                  className="text-sm font-medium px-3 py-1.5 rounded-md text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-50">
                  {t('common:actions.cancel')}
                </button>
                <button
                  onClick={() => send(confirm.mode === 'all' ? [{ action: 'update', packages: updatable.map(u => u.name) }] : selectionOps())}
                  disabled={!confirmChecked || sending}
                  className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-1.5 rounded-md text-white bg-brand hover:bg-brand-700 disabled:bg-slate-200 disabled:text-slate-400 dark:disabled:bg-slate-700 dark:disabled:text-slate-500">
                  {sending && <Loader2 size={13} className="animate-spin" />}
                  {sending ? t('ops:devices.update.sending') : t('ops:devices.update.send')}
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-slate-500 dark:text-slate-400">
                {t('ops:devices.update.selected_count', { count: selected.size })}
              </span>
              {atLimit && <span className="text-xs text-amber-600 dark:text-amber-400">{t('ops:devices.update.limit_hint')}</span>}
              <div className="ml-auto flex flex-wrap gap-2">
                <button onClick={() => setRollbackOpen(true)}
                  className="inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-md border border-volis-red/40 text-volis-red hover:bg-volis-red/5">
                  <Undo2 size={14} /> {t('ops:devices.update.rollback')}
                </button>
                <button onClick={() => { setSendError(null); setConfirm({ mode: 'all' }) }}
                  className="inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-md border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800">
                  {t('ops:devices.update.update_all')}
                </button>
                <button onClick={() => { setSendError(null); setConfirm({ mode: 'selected' }) }} disabled={selected.size === 0}
                  className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-1.5 rounded-md text-white bg-brand hover:bg-brand-700 disabled:bg-slate-200 disabled:text-slate-400 dark:disabled:bg-slate-700 dark:disabled:text-slate-500">
                  <RefreshCw size={14} /> {t('ops:devices.update.update_selected')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {rollbackOpen && (
        <ConfirmDialog
          title={t('ops:devices.update.rollback')}
          message={t('ops:devices.update.rollback_message')}
          confirmLabel={t('ops:devices.update.rollback_confirm')}
          danger
          onConfirm={sendRollback}
          onClose={() => setRollbackOpen(false)}
        />
      )}
    </div>,
    document.body
  )
}
